<template>
  <div>
    <v-alert
      v-if="showPreSettlementCreditBlock"
      type="error"
      outlined
      dense
      class="mb-2"
    >
      {{ preSettlementPreview.message }}
    </v-alert>
    <v-simple-table dense :class="'cartItems'">
      <template v-slot:default>
        <thead>
        <tr>
          <th class="text-center" width="70%">Product</th>
          <th class="text-center" width="10%">Quantity</th>
          <th class="text-center" width="10%">Rate</th>
          <th class="text-center" width="10%">Total</th>
        </tr>
        </thead>
        <tbody>
        <tr v-for="item in getCart().items" :key="item.item.id">
          <td>
            <product-info-cell
              :product="item.item"
              :buy-sell="buySell"
            ></product-info-cell>
            <v-btn
              link
              text
              bottom
              left
              x-small
              color="blue"
              class="pa-0"
              @click="removeItemFromCart(item)"
            >Remove
            </v-btn
            >
          </td>
          <td>
            <number-field
              :options="
                  item.item.allowFractionalQty
                    ? { type: `fractionalQty` }
                    : { type: `qty` }
                "
              :rules="getRulesForItem(item.item, buySell)"
              v-model="item.quantity"
              @input="updateItemQuantity(item, $event)"
              :min="getMin(item.item, buySell)"
              :max="getMax(item.item, buySell)"
              v-precision.group="getMaxAllowedQtyDecimals(item.item)"
            />
          </td>
          <td class="text-right">
            {{ computeUnitPrice(item) | toCurrency }}
          </td>
          <td class="text-right">
            {{ (computeUnitPrice(item) * item.quantity) | toCurrency }}
          </td>
        </tr>
        <tr>
          <td colspan="3">
            <v-btn
              link
              text
              bottom
              left
              x-small
              color="blue"
              @click="removeAllItemsFromCartConfirm"
              class="pa-0"
            >Remove All
            </v-btn
            >
            <span class="float-right text-no-wrap">Subtotal</span>
          </td>
          <td class="text-right">{{ totals().subtotal | toCurrency }}</td>
        </tr>
        <tr v-if="applySelectedPaymentMethodFee()">
          <td colspan="3" class="text-right">
              <span class="text-no-wrap"
              >{{ selectedPaymentMethodFeeLineText }}
                <v-tooltip v-if="paymentMethodFeeThreshold(selectedPaymentMethod)" bottom>
                  <template v-slot:activator="{ on, attrs }">
                    <v-icon color="primary" v-bind="attrs" v-on="on">
                      mdi-help-circle-outline
                    </v-icon>
                  </template>
                  <span
                  >This fee is waived for purchases of
                    {{ paymentMethodFeeThreshold(selectedPaymentMethod) | toCurrency }}
                    or more.</span
                  >
                </v-tooltip></span
              >
          </td>
          <td class="text-right">
            {{ selectedPaymentMethod.fee | toCurrency }}
          </td>
        </tr>
        <tr v-if="merchandiseProcessingFee">
          <td colspan="3">
            <span class="text-right text-no-wrap"
            >U.S. Customs Merchandise Processing Fee (MPF)
                <v-tooltip bottom>
                  <template v-slot:activator="{ on, attrs }">
                    <v-icon color="primary" v-bind="attrs" v-on="on">
                      mdi-help-circle-outline
                    </v-icon>
                  </template>
                  <span
                  >You are responsible for paying all import fees for this purchase order.
                    While there are no tariffs on investment-grade bullion, U.S. Customs & Border Protection applies a
                    Merchandise Processing Fee of 0.3464%, with a minimum charge of $32.71 and a maximum of $634.62 per
                    shipment. Please use the correct tariff codes to avoid incorrect duty charges. You are also
                    responsible for any additional duties and fees that may arise.</span
                  >
                </v-tooltip></span
            >
          </td>
          <td class="text-right">
            {{ merchandiseProcessingFee | toCurrency }}
          </td>
        </tr>
        <tr v-if="buySell === `buy` && getBuyHandlingFee > 0">
          <td colspan="3">
            <handling-tooltip></handling-tooltip>
          </td>
          <td class="text-right">
            {{ getBuyHandlingFee | toCurrency }}
          </td>
        </tr>
        <tr v-if="buySell === 'sell' && getSellHandlingFee > 0">
          <td colspan="3" class="text-right">
            <span class="text-no-wrap"
            >Small Order Processing Fee
                <v-tooltip bottom>
                  <template v-slot:activator="{ on, attrs }">
                    <v-icon color="primary" v-bind="attrs" v-on="on">
                      mdi-help-circle-outline
                    </v-icon>
                  </template>
                  <span
                  >Purchase orders below {{ getConfig('PO_HANDLING_FEE_THRESHOLD') | toCurrency }} are below wholesale minimums. This fee offsets the cost of processing this order while continuing to extend wholesale pricing.</span
                  >
                </v-tooltip>
            </span>
          </td>
          <td class="text-right">
            -{{ getSellHandlingFee | toCurrency }}
          </td>
        </tr>
        <tr v-if="showPreSettlementFee">
          <td colspan="3">
            <div class="d-flex align-center justify-space-between">
              <v-btn
                link
                text
                x-small
                color="blue"
                class="pa-0"
                data-test="pre-settlement-fee-opt-out"
                aria-label="Remove Pre-Settlement fee and switch to DOR"
                @click="optOutOfPreSettlementFee"
              >Remove
              </v-btn>
              <div class="d-inline-flex align-center justify-end text-right">
                <v-menu
                  open-on-hover
                  offset-y
                  left
                  :close-on-content-click="false"
                  max-width="340"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-chip
                      x-small
                      label
                      color="primary"
                      class="mr-3 pre-settlement-new-feature"
                      data-test="pre-settlement-new-feature"
                      aria-label="New feature: Pre-Settlement Payment. Click to learn more."
                      v-bind="attrs"
                      v-on="on"
                      @click="goToPreSettlementProgram"
                    >
                      NEW
                    </v-chip>
                  </template>
                  <v-card class="pa-3" max-width="340">
                    <div class="text-body-2 mb-2">
                      Pre-Settlement Payment is an optional early-payment option for approved
                      dealers. Payment will be released within one business day of confirmation.
                      Delivery of the PO is required by the date stated to avoid additional fees
                      and to adhere to the terms of this service.
                    </div>
                      <a
                        href="#/resources/pre-settlement-payment-program"
                        class="text-body-2 info--text text-decoration-underline"
                        data-test="pre-settlement-find-out-more"
                        @click.prevent="goToPreSettlementProgram"
                      >Click to find out more</a>
                  </v-card>
                </v-menu>
                <div>
                  <div>Pre-Settlement Payment</div>
                  <div class="font-italic grey--text text--secondary">
                    Deliver by {{ preSettlementDeliveryDate() }}
                  </div>
                </div>
              </div>
            </div>
          </td>
          <td class="text-right">
            -{{ preSettlementPreview.fee | toCurrency }}
          </td>
        </tr>
        <tr>
          <td colspan="3" class="text-right">
            <span class="font-weight-bold">Your Total</span>
          </td>
          <td>
              <span class="font-weight-bold float-right text-no-wrap">{{
                  totals().total | toCurrency
                }}</span>
          </td>
        </tr>
        </tbody>
      </template>
    </v-simple-table>
    <v-card
      v-if="showPreSettlementFinancing"
      outlined
      class="mt-3 financing-summary"
    >
      <v-card-text class="py-3">
        <div class="financing-summary__row">
          <span class="text-no-wrap">Total Pre-Settlement Availability</span>
          <span class="text-no-wrap font-weight-medium">
            {{ availableFinancing | toCurrency }}
          </span>
        </div>
        <div class="financing-summary__row mt-2">
          <span class="text-no-wrap">Pre-Settlement Availability after this Trade</span>
          <span class="text-no-wrap font-weight-medium">
            {{ financingAfterTransaction | toCurrency }}
          </span>
        </div>
        <div class="mt-2">
          <a
            href="#/resources/pre-settlement-payment-program"
            class="text-body-2 info--text text-decoration-underline"
            data-test="pre-settlement-availability-learn-more"
            @click.prevent="goToPreSettlementProgram"
          >Learn more about the Pre-Settlement Availability Program</a>
        </div>
      </v-card-text>
    </v-card>
  </div>
</template>

<script>
import orderMixin from '@/mixins/order'
import productMixin from '@/mixins/product'
import notifications from '@/mixins/notifications'
import {mapGetters, mapState} from 'vuex'
import NumberField from '@/components/NumberField'
import ProductInfoCell from '@/components/cart/ProductInfoCell'
import HandlingTooltip from '@/components/cart/HandlingTooltip'
import request from '@/util/request'
import {resolveDorPaymentMethod, titleModality} from '@/util/preSettlement'

export default {
  mixins: [productMixin, orderMixin, notifications],
  props: ['buySell'],
  data() {
    return {
      preSettlementPreview: null,
    }
  },
  components: {
    HandlingTooltip,
    NumberField,
    ProductInfoCell,
  },
  computed: {
    ...mapState({
      spotPrices: (state) => state.user.tradeTicketSpotPrices,
    }),
    ...mapGetters([
      'getBuyCart',
      'getSellCart',
      'getBuyHandlingFee',
      'getSellHandlingFee',
      'getSelectedTradingRole',
      'getSelectedCompany',
      'getConfig'
    ]),
    selectedPaymentMethod: {
      get() {
        return this.$store.getters['getCartField'](
          `${this.buySell}.selectedPaymentMethod`
        )
      },
    },
    merchandiseProcessingFee() {
      return this.getMerchandiseProcessingFee(this.buySell, this.getSelectedCompany, this.subtotal())
    },
    isPreSettlementPaymentMethodSelected() {
      return this.isPreSettlementPaymentMethod(this.selectedPaymentMethod)
    },
    selectedPaymentMethodFeeLineText() {
      if (this.isPreSettlementPaymentMethodSelected) {
        const title = String(this.selectedPaymentMethod?.title || '')
        return titleModality(title) === 'wire' ? 'Wire Fee' : 'ACH Fee'
      }
      return `${this.selectedPaymentMethod.title} Fee`
    },
    showPreSettlementFinancing() {
      return this.buySell === 'sell' &&
        this.isPreSettlementPaymentMethodSelected &&
        this.preSettlementPreview &&
        this.preSettlementPreview.availableCredit !== null &&
        this.preSettlementPreview.availableCredit !== undefined
    },
    availableFinancing() {
      return this.preSettlementPreview &&
        this.preSettlementPreview.availableCredit
    },
    financingAfterTransaction() {
      const preview = this.preSettlementPreview
      if (!preview || preview.availableCredit == null) {
        return null
      }

      const available = parseFloat(preview.availableCredit)
      const projected = parseFloat(preview.projectedTotal)
      return Math.round((available - projected + Number.EPSILON) * 100) / 100
    },
    showPreSettlementCreditBlock() {
      return this.showPreSettlementFinancing &&
        this.preSettlementPreview.creditBlocked
    },
    showPreSettlementFee() {
      return this.buySell === 'sell' &&
        this.preSettlementPreview &&
        !this.preSettlementPreview.creditBlocked &&
        parseFloat(this.preSettlementPreview.fee) > 0
    },
  },
  watch: {
    'selectedPaymentMethod.id': function () {
      this.fetchPreSettlementPreview()
    },
    'getSelectedCompany.id': function () {
      this.fetchPreSettlementPreview()
    },
    'getSellCart.items': {
      handler() {
        this.fetchPreSettlementPreview()
      },
      deep: true,
    },
  },
  mounted() {
    this.fetchPreSettlementPreview()
  },
  methods: {
    totals() {
      const cart = this.getCart()
      let subtotal = cart.items.reduce((accumulator, item) => {
        accumulator += this.computeUnitPrice(item) * item.quantity
        return accumulator
      }, 0)
      let total = this.subtotal()
      if (this.applySelectedPaymentMethodFee()) {
        total += parseFloat(cart.selectedPaymentMethod.fee)
      }
      if (this.buySell === `buy` && this.getBuyHandlingFee) {
        total += parseFloat(this.getBuyHandlingFee)
      }
      if (this.buySell === 'sell' && this.getSellHandlingFee) {
        total -= parseFloat(this.getSellHandlingFee)

      }
      if (this.buySell === 'sell' && this.merchandiseProcessingFee) {
        total += parseFloat(this.merchandiseProcessingFee)
      }
      if (this.showPreSettlementFee) {
        total -= parseFloat(this.preSettlementPreview.fee)
      }
      const result = {
        subtotal,
        total,
      }
      this.$emit('updateTotal', result)
      return result
    },
    getCart() {
      return this.buySell === 'buy' ? this.getBuyCart : this.getSellCart
    },
    computeUnitPrice: function (item) {
      const userConfiguredMarkups =
        item.retailFlatMarkup || item.retailPercentMarkup
          ? this.convertRetailMarkupsToProductMarkups(
            this.buySell,
            item.retailFlatMarkup,
            item.retailPercentMarkup
          )
          : {}
      return this.buySell === 'buy'
        ? this.computePrices(
          item.item,
          this.spotPrices,
          this.getSelectedCompany,
          this.getSelectedTradingRole,
          userConfiguredMarkups
        ).ask
        : this.computePrices(
          item.item,
          this.spotPrices,
          this.getSelectedCompany,
          this.getSelectedTradingRole,
          userConfiguredMarkups
        ).bid
    },
    subtotal() {
      const cart = this.getCart()
      return cart.items.reduce((accumulator, item) => {
        accumulator += this.computeUnitPrice(item) * item.quantity
        return accumulator
      }, 0)
    },
    applySelectedPaymentMethodFee() {
      const cart = this.getCart()
      if (
        cart.selectedPaymentMethod.fee &&
        (this.paymentMethodFeeThreshold(cart.selectedPaymentMethod) === null ||
          this.subtotal() < this.paymentMethodFeeThreshold(cart.selectedPaymentMethod))
      ) {
        return true
      } else {
        return false
      }
    },
    paymentMethodFeeThreshold(paymentMethod) {
      const title = String(paymentMethod.title || '').toLowerCase()
      if (title.includes('wire')) return 49999.99
      const feeWaived = paymentMethod.fee_waived
      if (feeWaived === null || feeWaived === undefined || feeWaived === '') return null
      return Math.round((Number(feeWaived) + Number.EPSILON) * 100) / 100
    },
    preSettlementDeliveryDate() {
      const date = new Date()
      date.setDate(date.getDate() + 7)
      const mm = String(date.getMonth() + 1).padStart(2, '0')
      const dd = String(date.getDate()).padStart(2, '0')
      return `${mm}/${dd}/${date.getFullYear()}`
    },
    async fetchPreSettlementPreview() {
      if (
        this.buySell !== 'sell' ||
        !this.selectedPaymentMethod.id ||
        !this.getSelectedCompany.id ||
        this.getSellCart.items.length === 0
      ) {
        this.preSettlementPreview = null
        this.$store.commit('updateCartField', {
          path: 'sell.preSettlementPreview',
          value: null,
        })
        return
      }
      try {
        this.preSettlementPreview = await request({
          url: `/purchase-orders/pre-settlement-preview/${this.getSelectedCompany.id}`,
          method: 'post',
          data: {
            paymentMethodId: this.selectedPaymentMethod.id,
            subtotal: this.subtotal(),
          },
        })
        this.$store.commit('updateCartField', {
          path: 'sell.preSettlementPreview',
          value: this.preSettlementPreview,
        })
      } catch (error) {
        this.preSettlementPreview = null
        this.$store.commit('updateCartField', {
          path: 'sell.preSettlementPreview',
          value: null,
        })
      }
    },
    removeAllItemsFromCartConfirm() {
      window._VMA.$emit('CONFIRM', {
        title: `Confirm Clear Trade`,
        message: `Are you sure you want remove all items from this trade?`,
        options: {},
        callback: (agree) => {
          if (agree) this.removeAllItemsFromCart()
        },
      })
    },
    removeAllItemsFromCart() {
      this.$store.dispatch('resetCartState', {
        buySell: this.buySell,
      })
    },
    removeItemFromCart(item) {
      this.$store.dispatch('removeItemFromCart', {
        item,
        buySell: this.buySell,
      })
    },
    optOutOfPreSettlementFee() {
      const dorPaymentMethod = resolveDorPaymentMethod(
        this.selectedPaymentMethod,
        this.getPaymentMethods(this.buySell)
      )
      if (!dorPaymentMethod) {
        this.showErrorNotification(
          'No DOR payment terms are available for this company. Contact the trading desk to update your payment terms.'
        )
        return
      }

      this.$store.commit('updateCartField', {
        path: `${this.buySell}.selectedPaymentMethod`,
        value: dorPaymentMethod,
      })
      this.preSettlementPreview = null
      this.$store.commit('updateCartField', {
        path: `${this.buySell}.preSettlementPreview`,
        value: null,
      })
    },
    goToPreSettlementProgram() {
      this.$emit('go-to-pre-settlement-program')
    },
    async updateItemQuantity(item, event) {
      const quantity = event
      try {
        await this.$store.dispatch('updateItemQuantity', {
          item: item.item,
          buySell: this.buySell,
          quantity,
        })
      } catch (error) {
        window._VMA.$emit('SHOW_SNACKBAR', {
          text: error.message,
          color: 'error',
        })
      }
    },
  },
}
</script>

<style>
.cartItems table {
  border: 1px solid #eaebed;
}

.cartItems table thead {
  background-color: #f7f8fa;
  color: #888e99;
  text-transform: uppercase;
}

.financing-summary {
  border-color: #d8dce3;
  background-color: #f7f8fa;
}

.financing-summary__row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
}
</style>

<style scoped>
.pre-settlement-new-feature {
  animation: pre-settlement-new-pulse 2s infinite;
  cursor: pointer;
}

@keyframes pre-settlement-new-pulse {
  0% {
    box-shadow: 0 0 0 0 rgba(25, 118, 210, 0.45);
  }
  70% {
    box-shadow: 0 0 0 8px rgba(25, 118, 210, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(25, 118, 210, 0);
  }
}
</style>
